import React from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { estaProgramado } from "./horarios";
import { etiquetaDeJornada, jornadasDeRueda, turnosDeJornada } from "./jornadas";
import { generarPlanillaMicrofono } from "./planillaMicrofono";
import { toastBigError } from "components/utils/toasts";

const AMBOS = "ambos";

// Pregunta de qué jornada de la rueda y de qué turno se genera la planilla de
// micrófono. Los turnos no se cargan: se deducen de los horarios de la jornada,
// así que la opción "Ambos" solo aparece cuando ese día se juega en dos turnos.
export default function PlanillaMicrofonoModal({
  isOpen,
  round,
  torneoNombre,
  torneoAno,
  teams = [],
  sedes = [],
  onClose,
}) {
  const jornadas = React.useMemo(() => jornadasDeRueda(round), [round]);
  const [dia, setDia] = React.useState("");
  const [turnoId, setTurnoId] = React.useState(AMBOS);
  const [generando, setGenerando] = React.useState(false);

  // Al abrir se propone la jornada más reciente.
  React.useEffect(() => {
    if (isOpen) setDia(jornadas.length ? jornadas[0].dia : "");
  }, [isOpen, jornadas]);

  const jornada = jornadas.find((j) => j.dia === dia) || null;
  // Los partidos sin cancha u horario no entran en la planilla, así que tampoco
  // cuentan para deducir los turnos.
  const turnos = React.useMemo(
    () => (jornada ? turnosDeJornada(jornada.partidos.filter(estaProgramado)) : []),
    [jornada]
  );

  // Con un solo turno no hay nada que elegir; con dos, se arranca en "Ambos".
  React.useEffect(() => {
    setTurnoId(turnos.length === 1 ? turnos[0].id : AMBOS);
  }, [turnos]);

  const sedeNombre = (jornada && (sedes.find((s) => s.value === jornada.sede) || {}).label) || "";
  const turnosElegidos = turnoId === AMBOS ? turnos : turnos.filter((t) => t.id === turnoId);

  const generar = async () => {
    if (!jornada || turnosElegidos.length === 0) return;
    setGenerando(true);
    try {
      await generarPlanillaMicrofono({
        torneoNombre,
        torneoAno,
        numero: jornada.numero,
        dia: jornada.dia,
        sedeNombre,
        turnos: turnosElegidos,
        teams,
      });
      onClose();
    } catch (ex) {
      toastBigError("No se pudo generar la planilla", "Intentá de nuevo en un momento.");
    } finally {
      setGenerando(false);
    }
  };

  const opcionesDeTurno = turnos.length > 1 ? [...turnos, { id: AMBOS, nombre: "Ambos" }] : turnos;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>Planilla Micrófono</ModalHeader>
      <ModalBody>
        {jornadas.length === 0 ? (
          <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
            Las zonas de esta rueda todavía no tienen fechas con día asignado.
          </p>
        ) : (
          <React.Fragment>
            <div className="form-group">
              <label className="font-weight-bold" style={{ fontSize: "0.8125rem" }}>
                Fecha
              </label>
              <select className="form-control" value={dia} onChange={(e) => setDia(e.target.value)}>
                {jornadas.map((j) => (
                  <option key={j.dia} value={j.dia}>
                    {etiquetaDeJornada(j)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group mb-0">
              <label className="font-weight-bold" style={{ fontSize: "0.8125rem" }}>
                Turno
              </label>
              {turnos.length === 0 ? (
                <p className="text-muted mb-0" style={{ fontSize: "0.8125rem" }}>
                  Esa fecha todavía no tiene partidos con cancha y horario asignados.
                </p>
              ) : (
                <div className="d-flex flex-wrap" style={{ gap: "0.5rem" }}>
                  {opcionesDeTurno.map((t) => (
                    <Button
                      key={t.id}
                      className="btn-arhock btn-sm my-0"
                      style={{ opacity: turnoId === t.id ? 1 : 0.55 }}
                      onClick={() => setTurnoId(t.id)}
                    >
                      {t.nombre}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </React.Fragment>
        )}
      </ModalBody>
      <ModalFooter>
        <Button className="btn-arhock btn-sm my-0" style={{ opacity: 0.55 }} onClick={onClose}>
          Cancelar
        </Button>
        <Button
          className="btn-arhock btn-sm my-0"
          onClick={generar}
          disabled={turnosElegidos.length === 0 || generando}
        >
          {generando ? "Generando..." : "Generar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
